import { Point2D, Element } from '../types';

/**
 * 检测点是否在端点附近
 */
export function getPointNearEndpoint(
  point: Point2D,
  element: Element,
  threshold: number = 8
): number | null {
  for (let i = 0; i < element.points.length; i++) {
    const p = element.points[i];
    const dist = Math.sqrt(Math.pow(point.x - p.x, 2) + Math.pow(point.y - p.y, 2));
    if (dist < threshold) {
      return i;
    }
  }
  return null;
}

/**
 * 检测点是否在线段附近
 */
export function isPointNearSegment(
  point: Point2D,
  p1: Point2D,
  p2: Point2D,
  threshold: number = 5
): boolean {
  // 计算点到线段的距离
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const lengthSq = dx * dx + dy * dy;
  
  if (lengthSq === 0) {
    // 线段退化为点
    const dist = Math.sqrt(Math.pow(point.x - p1.x, 2) + Math.pow(point.y - p1.y, 2));
    return dist < threshold;
  }
  
  // 计算投影参数 t
  let t = ((point.x - p1.x) * dx + (point.y - p1.y) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  
  // 计算投影点
  const projX = p1.x + t * dx;
  const projY = p1.y + t * dy;
  
  // 计算距离
  const dist = Math.sqrt(Math.pow(point.x - projX, 2) + Math.pow(point.y - projY, 2));
  return dist < threshold;
}

/**
 * 检测点是否在元素上（包括端点和线段）
 */
export function isPointOnElement(point: Point2D, element: Element, threshold: number = 5): boolean {
  // 对于model3d、circle等填充型元素，检查点是否在内部区域
  if (element.type === 'model3d' || element.type === 'circle') {
    return isPointInElement(point, element);
  }
  
  // 先检查端点
  if (getPointNearEndpoint(point, element, threshold) !== null) {
    return true;
  }
  
  // 检查线段
  for (let i = 0; i < element.points.length - 1; i++) {
    if (isPointNearSegment(point, element.points[i], element.points[i + 1], threshold)) {
      return true;
    }
  }
  
  // 对于封闭图形，检查最后一个点到第一个点的线段
  if (element.type === 'area' || element.type === 'polygon') {
    const lastIdx = element.points.length - 1;
    if (lastIdx > 0 && isPointNearSegment(point, element.points[lastIdx], element.points[0], threshold)) {
      return true;
    }
  }
  
  return false;
}

/**
 * 检测点是否在元素内部（用于填充型元素）
 */
export function isPointInElement(point: Point2D, element: Element): boolean {
  if (element.points.length < 3) return false;
  
  // 对于model3d，检查点是否在旋转后的矩形内
  if (element.type === 'model3d') {
    const minX = Math.min(...element.points.map(p => p.x));
    const maxX = Math.max(...element.points.map(p => p.x));
    const minY = Math.min(...element.points.map(p => p.y));
    const maxY = Math.max(...element.points.map(p => p.y));
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    
    // 如果有旋转角度，需要将点转换到未旋转的坐标系
    if (element.rotation) {
      const rotationRad = -(element.rotation * Math.PI) / 180; // 反向旋转
      const dx = point.x - centerX;
      const dy = point.y - centerY;
      const cos = Math.cos(rotationRad);
      const sin = Math.sin(rotationRad);
      const localX = centerX + (dx * cos - dy * sin);
      const localY = centerY + (dx * sin + dy * cos);
      
      // 检查转换后的点是否在未旋转的矩形内
      return localX >= minX && localX <= maxX && localY >= minY && localY <= maxY;
    }
    
    // 没有旋转，直接检查
    return point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
  }
  
  // 对于circle，检查点是否在圆内
  if (element.type === 'circle' && element.points.length >= 2) {
    const center = element.points[0];
    const edge = element.points[1];
    const radius = Math.sqrt(
      Math.pow(edge.x - center.x, 2) + Math.pow(edge.y - center.y, 2)
    );
    const distance = Math.sqrt(
      Math.pow(point.x - center.x, 2) + Math.pow(point.y - center.y, 2)
    );
    return distance <= radius;
  }
  
  // 对于多边形，使用射线法检测点是否在内部
  if (element.type === 'polygon' || element.type === 'area') {
    let inside = false;
    const points = element.points;
    
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].x;
      const yi = points[i].y;
      const xj = points[j].x;
      const yj = points[j].y;
      
      const intersect = ((yi > point.y) !== (yj > point.y)) &&
        (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
      
      if (intersect) inside = !inside;
    }
    
    return inside;
  }
  
  return false;
}
